import { addDays, format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

/**
 * Parse a calendar date string as local midnight.
 * Prefer this over `new Date('yyyy-MM-dd')`, which is UTC and shifts the day in negative offsets.
 */
export function parseDateOnlyString(raw: string): Date | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (/^\d{8}$/.test(trimmed)) {
    const y = Number(trimmed.slice(0, 4));
    const m = Number(trimmed.slice(4, 6));
    const d = Number(trimmed.slice(6, 8));
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const y = Number(ymd[1]);
    const mo = Number(ymd[2]);
    const d = Number(ymd[3]);
    const dt = new Date(y, mo - 1, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  const normalized = /^\d{4}-\d{2}-\d{2} \d/.test(trimmed)
    ? trimmed.replace(' ', 'T')
    : trimmed;
  const dt = new Date(normalized);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Alias kept for subscription date formatting callers. */
export function parseSubscriptionDateString(raw: string): Date | null {
  return parseDateOnlyString(raw);
}

export type FormatSubscriptionDateDisplayOptions = {
  locale?: string;
  intlOptions?: Intl.DateTimeFormatOptions;
  emptyDisplay?: string;
  invalidDisplay?: string;
};

export function formatSubscriptionDateDisplay(
  raw: string | null | undefined,
  options?: FormatSubscriptionDateDisplayOptions,
): string {
  const empty = options?.emptyDisplay ?? '-';
  if (raw == null || !String(raw).trim()) return empty;

  const trimmed = String(raw).trim();
  const parsed = parseSubscriptionDateString(trimmed);
  if (!parsed) {
    return options?.invalidDisplay ?? trimmed;
  }

  const { locale, intlOptions } = options ?? {};
  const useExplicitIntl = locale !== undefined || intlOptions !== undefined;
  if (useExplicitIntl) {
    return parsed.toLocaleDateString(
      locale,
      intlOptions ?? { year: 'numeric', month: 'short', day: 'numeric' },
    );
  }
  return parsed.toLocaleDateString();
}

//mapping of languages to date-fns locales
const localeMap = {
  'es': es,
  'en': enUS,
};
//ONLY variable used for sales closing cutoff hour
export const SALES_CLOSING_CUTOFF_HOUR = 4;

//formats the date according to the current language
export const formatDate = (date: Date, formatString: string, language: string = 'en'): string => {
  const locale = localeMap[language as keyof typeof localeMap] || enUS;
  return format(date, formatString, { locale });
};

//formats the short date (e.g. "15/01/2025" for spanish, "01/15/2025" for english)
export const formatShortDate = (date: Date, language: string = 'en'): string => {
  if (language === 'es') {
    return formatDate(date, 'dd/MM/yyyy', language);
  }
  return formatDate(date, 'MM/dd/yyyy', language);
};

//formats the long date (e.g. "15 de enero de 2025" for spanish)
export const formatLongDate = (date: Date, language: string = 'en'): string => {
  if (language === 'es') {
    return formatDate(date, 'd \'de\' MMMM \'de\' yyyy', language);
  }
  return formatDate(date, 'MMMM d, yyyy', language);
};

//formats the date and time
export const formatDateTime = (date: Date, language: string = 'en'): string => {
  if (language === 'es') {
    return formatDate(date, 'dd/MM/yyyy HH:mm', language);
  }
  return formatDate(date, 'MM/dd/yyyy HH:mm', language);
};

//formats only the time
export const formatTime = (date: Date, language: string = 'en'): string => {
  return formatDate(date, 'HH:mm', language);
};

/**
 * Parses DB / API timestamps, including Postgres forms like
 * `2026-05-18 01:40:30.059+00` that some engines reject as-is.
 */
export const parseTimestamp = (raw: string | Date | null | undefined): Date | undefined => {
  if (raw == null || raw === '') return undefined;
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? undefined : raw;
  }

  let normalized = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2} /.test(normalized)) {
    normalized = normalized.replace(' ', 'T');
  }
  // `+00` / `-05` → `+00:00` / `-05:00`
  normalized = normalized.replace(/([+-]\d{2})$/, '$1:00');

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

//formats a date relative to today (today, yesterday, tomorrow, etc)
export const formatRelativeDate = (date: Date, language: string = 'en'): string => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  const diffTime = targetDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return language === 'es' ? 'Hoy' : 'Today';
  } else if (diffDays === -1) {
    return language === 'es' ? 'Ayer' : 'Yesterday';
  } else if (diffDays === 1) {
    return language === 'es' ? 'Mañana' : 'Tomorrow';
  } else {
    return formatShortDate(date, language);
  }
};

//gets the closing date for a sale
export const getClosingDate = (saleDate: Date): string => {
  const date = new Date(saleDate);
  const hour = date.getHours();
  
 //if the sale occurs before the cutoff hour, it belongs to the previous day
  if (hour < SALES_CLOSING_CUTOFF_HOUR) {
    const previousDay = new Date(date);
    previousDay.setDate(previousDay.getDate() - 1);
    return format(previousDay, 'yyyy-MM-dd');
  }
  
  // if the sale occurs after the cutoff hour, it belongs to the current day
  return format(date, 'yyyy-MM-dd');
};

//checks if a sale belongs to a specific closing date
export const belongsToClosingDate = (
  saleDate: Date, 
  closingDate: Date | string, 
): boolean => {
  const saleClosingDate = getClosingDate(saleDate);
  const targetDateStr = typeof closingDate === 'string' 
    ? closingDate 
    : format(closingDate, 'yyyy-MM-dd');
  //checks if the sale belongs to the closing date
  return saleClosingDate === targetDateStr;
};

/**
 * Returns a timestamp at the start of the next closing day (cutoff hour).
 * Used when a sale would fall on a day that already has a cash closure:
 * the sale is assigned to the next closing date.
 */
export const getNextClosingDateTimestamp = (closingDateStr: string): Date => {
  const [y, m, d] = closingDateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const nextDay = addDays(date, 1);
  nextDay.setHours(SALES_CLOSING_CUTOFF_HOUR, 0, 0, 0);
  return nextDay;
};


//custom hook to use the date formatting with the language context
export const useDateFormat = () => {
  return {
    formatDate,
    formatShortDate,
    formatLongDate,
    formatDateTime,
    formatTime,
    formatRelativeDate
  };
};
